import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import { ButtonGroup, Notice, toolkitCx } from "@sundae/ui-toolkit";
import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { ADA_METADATA } from "@sundaeswap/core";
import cx from "classnames";
import noop from "lodash/noop";
import { FC, useCallback, useMemo } from "react";

import { useAdaBalance } from "../../../hooks/useAdaBalance";
import { useIsWalletConnected } from "../../../hooks/useIsWalletConnected";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useTasteTestTx } from "../../../pages/TasteTestDetail/hooks/useTasteTestTx";
import {
  EOrderFlowState,
  EOrderTransactionBuilderState,
  OrderActions,
  useOrderContext,
} from "../../../stores/OrderContext";
import { IAssetMetaData } from "../../../types/Asset.types";
import { getAssetName } from "../../../utils/assets.utils";
import AssetAmountBox from "../../AssetAmountBox";
import ConnectWalletButton from "../../ConnectWalletButton";
import FadeInBasic from "../../FadeInView/FadeInBasic";
import TasteTestDepositOrderPreview from "../TasteTestDepositOrderPreview";

const TasteTestDepositSetup: FC = () => {
  const { t } = useI18N("taste-test");
  const {
    derived: { depositAssetAmount, depositAssetAmountExceedsBalance, hasContribution },
    handlers: { setDepositValue, setShowCurrentContribution },
    state: {
      actionType,
      tasteTest,
      tasteTestStatus: { isTasteTestClosing, isTasteTestPending, isTasteTestInactive },
      tasteTestPersonalization,
    },
  } = useTasteTestContext();
  const { submitTx } = useTasteTestTx({ type: "deposit" });
  const {
    state: {
      derived: { transaction },
      flowData: { orderConsent },
    },
    dispatch,
  } = useOrderContext();
  const isWalletConnected = useIsWalletConnected();
  const { availableAdaBalance } = useAdaBalance();
  const adaAsset = useMemo(() => {
    if (isWalletConnected && availableAdaBalance?.metadata) return availableAdaBalance;

    return {
      metadata: ADA_METADATA,
      quantity: new AssetAmount(0, ADA_METADATA),
    } as IMappedAsset<IAssetMetaData>;
  }, [availableAdaBalance, isWalletConnected]);

  const primaryButtonLabel = useMemo(() => {
    if (depositAssetAmountExceedsBalance) {
      return t("insufficientBalance", {
        context: "asset",
        ns: "common",
        asset: getAssetName(ADA_METADATA),
      });
    }

    if (isTasteTestClosing) {
      return t("detail.widget.button.closing");
    }

    if (!depositAssetAmount?.amount) {
      return t("detail.widget.button.enterAmount");
    }

    if (isTasteTestInactive || isTasteTestPending) {
      return t("detail.widget.button.inactiveOrPending");
    }

    if (hasContribution || (depositAssetAmount && depositAssetAmount.amount !== 0n)) {
      return t("detail.widget.button.add");
    }

    return "";
  }, [
    depositAssetAmount,
    depositAssetAmountExceedsBalance,
    hasContribution,
    isTasteTestClosing,
    isTasteTestInactive,
    isTasteTestPending,
    t,
  ]);

  const isButtonDisabled = useMemo(
    () =>
      depositAssetAmountExceedsBalance ||
      isTasteTestInactive ||
      isTasteTestPending ||
      !depositAssetAmount?.amount ||
      !orderConsent.checked ||
      transaction?.state === EOrderTransactionBuilderState.BUILD,
    [
      depositAssetAmount?.amount,
      depositAssetAmountExceedsBalance,
      isTasteTestInactive,
      isTasteTestPending,
      orderConsent.checked,
      transaction?.state,
    ]
  );

  const handleBackClick = useCallback(() => {
    if (actionType === "update") {
      if (depositAssetAmount) {
        setDepositValue("0");
      }
      setShowCurrentContribution(true);
      OrderActions.setOrderFlowState(EOrderFlowState.initial, dispatch);
      OrderActions.setOrderConsent({ checked: false }, dispatch);
    }
  }, [actionType, depositAssetAmount, dispatch]);

  const handleValueChange = useCallback(
    (value: string) => {
      if (isTasteTestInactive) return;
      setDepositValue(value);
    },
    [isTasteTestInactive]
  );

  return (
    <div className={cx("flex w-full flex-col gap-6", toolkitCx.layout.spacing.both.md)}>
      <AssetAmountBox
        activeAsset={adaAsset}
        assets={[]}
        assetValue={depositAssetAmount?.value.toString() ?? ""}
        autoFocus={!isTasteTestInactive}
        isSelectDisabled
        setActiveAsset={noop}
        setValueAsset={handleValueChange}
        topline={t("detail.widget.assetAmountBox.topline")}
      />
      {!depositAssetAmount?.amount && (
        <Notice
          title={t("detail.widget.deposit.initialNotice.title", {
            assetName: tasteTest?.asset?.ticker,
          })}
          type="info"
          size="xs"
        >
          {t("detail.widget.deposit.initialNotice.desc", {
            name: tasteTest?.projectDetails.name,
            poolName: `ADA-${tasteTest?.asset?.ticker}`,
          })}
        </Notice>
      )}

      <div className={cx("relative flex flex-col gap-6", toolkitCx.layout.spacing.horizontal.md)}>
        {!!depositAssetAmount?.amount && <TasteTestDepositOrderPreview />}
        <FadeInBasic className="sticky bottom-4 z-10" transitionDelay={150}>
          {
            // TODO: fix button group style props
            isWalletConnected ? (
              <ButtonGroup
                primaryButtonConfig={{
                  buttonProps: {
                    className: "hover:-translate-y-[2px] group",
                    disabled: isButtonDisabled,
                    onClick: () => submitTx(),
                    style: { backgroundColor: tasteTestPersonalization?.primaryColor || "" },
                  },
                  label: primaryButtonLabel,
                }}
                {...(hasContribution &&
                  !isTasteTestInactive && {
                    secondaryButtonConfig: {
                      buttonProps: {
                        className: "hover:-translate-y-[2px] group",
                        onClick: handleBackClick,
                      },
                      icon: ArrowLeftIcon,
                      iconProps: {
                        className: cx(
                          "group-hover:scale-110",
                          toolkitCx.layout.transition.transform
                        ),
                      },
                      label: t("back", { ns: "common" }),
                    },
                  })}
                size="md"
                widthRatio={["3/4", "1/4"]}
                withBackdropBlur
              />
            ) : (
              <ConnectWalletButton className="w-full" size="md" />
            )
          }
        </FadeInBasic>
      </div>
    </div>
  );
};

export default TasteTestDepositSetup;
